import {useCallback} from 'react'
import {View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'

import {type NavigationProp} from '#/lib/routes/types'
import {type MatrixRoomSummary} from '#/state/matrix/utils'
import {useProfileQuery} from '#/state/queries/profile'
import * as Toast from '#/view/com/util/Toast'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Text} from '#/components/Typography'

export function SecureRequestListItem({
  room,
  onAccept,
  onReject,
}: {
  room: MatrixRoomSummary
  onAccept: (roomId: string) => Promise<void>
  onReject: (roomId: string) => Promise<void>
}) {
  const {_} = useLingui()
  const t = useTheme()
  const navigation = useNavigation<NavigationProp>()
  const otherDid = room.otherMemberDids[0]
  const {data: profile} = useProfileQuery({did: otherDid})

  const displayName = profile?.displayName || profile?.handle || room.name
  const handle = profile?.handle ? `@${profile.handle}` : ''

  const handleAccept = useCallback(async () => {
    try {
      await onAccept(room.roomId)
      navigation.navigate('SecureChat', {roomId: room.roomId})
    } catch {
      Toast.show(_(msg`Failed to accept chat request`), 'xmark')
    }
  }, [onAccept, room.roomId, navigation, _])

  const handleReject = useCallback(async () => {
    try {
      await onReject(room.roomId)
    } catch {
      Toast.show(_(msg`Failed to decline chat request`), 'xmark')
    }
  }, [onReject, room.roomId, _])

  return (
    <View
      style={[
        a.flex_row,
        a.align_start,
        a.px_lg,
        a.py_md,
        a.gap_md,
        a.border_b,
        t.atoms.border_contrast_low,
      ]}>
      <UserAvatar type="user" size={52} avatar={profile?.avatar} />
      <View style={[a.flex_1]}>
        <Text
          emoji
          numberOfLines={1}
          style={[a.text_md, t.atoms.text, a.font_bold, {lineHeight: 21}]}>
          {displayName}
        </Text>
        {handle ? (
          <Text
            numberOfLines={1}
            style={[a.text_sm, t.atoms.text_contrast_medium, a.pb_xs]}>
            {handle}
          </Text>
        ) : null}
        {room.lastMessage && (
          <Text
            emoji
            numberOfLines={2}
            style={[a.text_sm, a.leading_snug, t.atoms.text_contrast_high]}>
            {room.lastMessage}
          </Text>
        )}
        <View style={[a.flex_row, a.gap_sm, a.pt_md]}>
          <Button
            label={_(msg`Accept chat request`)}
            size="tiny"
            color="secondary_inverted"
            variant="solid"
            onPress={handleAccept}>
            <ButtonText>
              <Trans>Accept</Trans>
            </ButtonText>
          </Button>
          <Button
            label={_(msg`Decline chat request`)}
            size="tiny"
            color="secondary"
            variant="solid"
            onPress={handleReject}>
            <ButtonText>
              <Trans>Decline</Trans>
            </ButtonText>
          </Button>
        </View>
      </View>
    </View>
  )
}
